import React, { useContext, useState, useRef, useEffect } from 'react';
import { FormContext } from '../../context/FormContext';
import Input from '../CustomInput';
import { IoTerminal } from "react-icons/io5";
import { FaBuilding } from "react-icons/fa";
import { FaFileCircleCheck } from "react-icons/fa6";
import { validateTaxId, fetchBillingDetails } from '../../api/api'; 

const Step2 = ({ nextStep, prevStep }) => {
  const { formData, updateFormData } = useContext(FormContext);
  const orderLines = formData.orderLines;
  
  const [errors, setErrors] = useState([]);
  const [validationLoading, setValidationLoading] = useState({});
  const [validationError, setValidationError] = useState({});
  const [validationWarning, setValidationWarning] = useState({});
  
  const stepTitleRef = useRef(null);
  const firstErrorRef = useRef(null);
  const shouldFocusErrorRef = useRef(false);

  // Regex to validate the Tax ID (CNPJ) format
  const taxIdRegex = /^\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}$/;

  // Formats the Tax ID as: 00.000.000/0000-00
  const formatTaxId = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    let formatted = '';

    if (digits.length >= 1) {
      formatted += digits.slice(0, 2);
    }
    if (digits.length >= 2) {
      formatted += '.';
    }
    if (digits.length > 2) {
      formatted += digits.slice(2, 5);
    }
    if (digits.length >= 5) {
      formatted += '.';
    }
    if (digits.length > 5) {
      formatted += digits.slice(5, 8);
    }
    if (digits.length >= 8) {
      formatted += '/';
    }
    if (digits.length > 8) {
      formatted += digits.slice(8, 12);
    }
    if (digits.length >= 12) {
      formatted += '-';
    }
    if (digits.length > 12) {
      formatted += digits.slice(12, 14);
    }

    return formatted;
  };

  const handleChange = (index, field, value) => {
    const updatedOrderLines = orderLines.map((orderLine, i) => {
      if (i === index) {
        const updatedLine = { ...orderLine, [field]: value };
        if (field === 'taxId') {
          value = formatTaxId(value);
          updatedLine.taxId = value;
          updatedLine.isValidated = false; // Invalida validação ao editar
          setValidationWarning(prev => ({ ...prev, [index]: '' }));
        }
        return updatedLine;
      }
      return orderLine;
    });
    updateFormData({ orderLines: updatedOrderLines });
    setErrors(prev => {
      const newErrors = [...prev];
      if (newErrors[index]) newErrors[index][field] = null;
      return newErrors;
    });
  };

  // Handler para tratar o backspace no campo Tax ID
  const handleKeyDownTaxId = (index, event) => {
    if (event.key === 'Backspace') {
      const separators = ['.', '/', '-'];
      const input = event.target;
      const pos = input.selectionStart;
      const value = orderLines[index].taxId;
      if (pos > 0 && separators.includes(value[pos - 1])) {
        event.preventDefault();
        const digits = value.replace(/\D/g, '');
        const newDigits = digits.slice(0, -1);
        const newFormatted = formatTaxId(newDigits);
        handleChange(index, 'taxId', newFormatted);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const isTaxIdValid = orderLines[index].taxId ? taxIdRegex.test(orderLines[index].taxId) : false;
      if (isTaxIdValid && !validationLoading[index]) {
        handleValidateTaxId(index);
      }
    }
  };

  // Validates the Tax ID via the API
  const handleValidateTaxId = async (index) => {
    const taxId = orderLines[index].taxId;
    if (!taxId) {
      setValidationError(prev => ({ ...prev, [index]: 'Por favor insira um CNPJ' }));
      return;
    }
    setValidationError(prev => ({ ...prev, [index]: '' }));
    setValidationWarning(prev => ({ ...prev, [index]: '' }));
    setValidationLoading(prev => ({ ...prev, [index]: true }));
    try {
      let formattedTaxId = await validateTaxId(taxId);
      formattedTaxId = formatTaxId(formattedTaxId);

      let companyName = '';
      try {
        const details = await fetchBillingDetails(formattedTaxId);
        if (details && details.company) {
          // Prefer alias; fall back to the capitalized first word of the name
          if (details.alias) {
            companyName = details.alias;
          } else if (details.company.name) {
            const firstWord = details.company.name.split(' ')[0];
            companyName = firstWord.charAt(0).toUpperCase() + firstWord.slice(1).toLowerCase();
          }
        }
      } catch (detailsError) {
        if (detailsError.message === 'Muitas requisições. Tente novamente em alguns instantes') {
          setValidationWarning(prev => ({ ...prev, [index]: 'Limite de requisições atingido. Preencha o nome da companhia manualmente.' }));
        } else {
          console.warn('[Step2] fetchBillingDetails failed, company name must be filled in manually:', detailsError);
          setValidationWarning(prev => ({ ...prev, [index]: 'Não foi possível obter o nome da companhia automaticamente. Preencha manualmente.' }));
        }
      }

      const updatedOrderLines = orderLines.map((ol, i) =>
        i === index ? { ...ol, taxId: formattedTaxId, name: companyName, isValidated: true } : ol
      );
      updateFormData({ orderLines: updatedOrderLines });
      setErrors(prev => {
        const newErrors = [...prev];
        if (newErrors[index]) newErrors[index].taxId = null;
        return newErrors;
      });
    } catch (error) {
      setValidationError(prev => ({ ...prev, [index]: error.message }));
      const updatedOrderLines = orderLines.map((ol, i) =>
        i === index ? { ...ol, isValidated: false, name: '' } : ol
      );
      updateFormData({ orderLines: updatedOrderLines });
    } finally {
      setValidationLoading(prev => ({ ...prev, [index]: false }));
    }
  };  

  // Focus management - focuses the title when the step mounts
  useEffect(() => {
    if (stepTitleRef.current) {
      stepTitleRef.current.focus();
    }
  }, []);

  // Focus management - focuses the first error when validation errors are present
  useEffect(() => {
    if (shouldFocusErrorRef.current && errors.length > 0 && firstErrorRef.current) {
      firstErrorRef.current.focus();
      shouldFocusErrorRef.current = false;
    }
  }, [errors]);

  const handleNext = () => {
    let isError = false;
    const newErrors = [];
    setValidationError({});
    orderLines.forEach((orderLine, index) => {
      let error = {};
      if (!orderLine.taxId) {
        error.taxId = 'CNPJ é obrigatório';
        isError = true;
      } else if (!taxIdRegex.test(orderLine.taxId)) {
        error.taxId = 'Formato do CNPJ inválido (00.000.000/0000-00)';
        isError = true;
      } else if (!orderLine.isValidated) {
        error.taxId = 'Por favor, valide o CNPJ';
        isError = true;
      }
      if (!orderLine.name) {
        error.name = 'Nome da Companhia é obrigatório';
        isError = true;
      } else if (orderLine.name.trim().length > 100) {
        error.name = 'Nome da Companhia não pode ter mais de 100 caracteres';
        isError = true;
      }
      const shareholderCountNum = Number(orderLine.shareholderCount);
      if (!orderLine.shareholderCount) {
        error.shareholderCount = 'Número de Acionistas é obrigatório';
        isError = true;
      } else if (!Number.isInteger(shareholderCountNum) || shareholderCountNum <= 0) {
        error.shareholderCount = 'Informe um número inteiro válido, por favor (mínimo 1).';
        isError = true;
      } else if (shareholderCountNum > 100000) {
        error.shareholderCount = 'Número de Acionistas não pode ser superior a 100 000.';
        isError = true;
      }
      newErrors[index] = error;
    });

    // Duplicate TaxId check
    const taxIds = orderLines.map(l => (l.taxId || '').replace(/\D/g, ''));
    taxIds.forEach((id, index) => {
      if (id && taxIds.indexOf(id) !== index) {
        newErrors[index] = { ...newErrors[index], taxId: 'CNPJ duplicado' };
        isError = true;
      }
    });

    // Duplicate Name check
    const names = orderLines.map(l => (l.name || '').trim().toLowerCase());
    names.forEach((name, index) => {
      if (name && names.indexOf(name) !== index) {
        newErrors[index] = { ...newErrors[index], name: 'Nome duplicado' };
        isError = true;
      }
    });

    setErrors(newErrors);
    if (isError) {
      shouldFocusErrorRef.current = true;
    }
  
    if (!isError) {
      nextStep();
    }
  };

  return (
    <div>
      <h2 
        ref={stepTitleRef}
        tabIndex="-1"
        className="text-xl text-white font-bold mb-4 focus:outline-none"
      >
        2º Passo: Detalhes da Companhia
      </h2>
      {orderLines.map((company, index) => {
        const isTaxIdValid = company.taxId ? taxIdRegex.test(company.taxId) : false;
        const taxIdError = validationError[index] || (errors[index] && errors[index].taxId);
        const hasError = errors[index] && Object.keys(errors[index]).length > 0;
        const isFirstError = index === 0 && hasError;

        return (
          <div key={index} className="mb-6 p-4 border rounded">
            <h3 className="text-lg text-white font-semibold mb-2">Companhia {index + 1}</h3>
            <div className="mb-4">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <Input 
                    ref={isFirstError ? firstErrorRef : null}
                    label={"CNPJ"}
                    value={company.taxId}
                    onChange={(e) => handleChange(index, 'taxId', e.target.value)}
                    onKeyDown={(e) => handleKeyDownTaxId(index, e)}
                    placeholder={'00.000.000/0000-00'}
                    pattern={"\\d{2}\\.?\\d{3}\\.?\\d{3}/?\\d{4}-?\\d{2}"}
                    error={taxIdError} 
                    icon={<IoTerminal className="size-4" />}
                  />
                  {!taxIdError && (
                    <p className="mt-1 text-xs text-gray-400" id={`cnpj-help-${index}`}>
                      Formato: 00.000.000/0000-00. Pressione Enter para validar.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="mt-8 h-[37.6px] flex justify-center items-center gap-x-2 rounded-md bg-[#008000] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm 
                             hover:bg-[#0F4D0F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#125c12]"
                  onClick={() => handleValidateTaxId(index)}
                  disabled={validationLoading[index] || !isTaxIdValid}
                  aria-label={`Validar CNPJ da companhia ${index + 1}`}
                  aria-busy={validationLoading[index] ? "true" : "false"}
                >
                  {validationLoading[index] ? 'Validando...' : 'Validar'}
                </button>
              </div>
              <div aria-live="polite" aria-atomic="true">
                {validationLoading[index] && (
                  <p className="mt-1 text-sm text-blue-600">
                    Validando CNPJ...
                  </p>
                )}
                {!taxIdError && company.taxId && company.isValidated && (
                  <p className="mt-1 text-sm text-green-600">
                    CNPJ validado com sucesso
                  </p>
                )}
                {validationWarning[index] && (
                  <p className="mt-1 text-sm text-yellow-400">
                    {validationWarning[index]}
                  </p>
                )}
              </div>
            </div>
            <div className="mb-4">
              <Input 
                label={"Nome da Companhia"}
                value={company.name}
                onChange={(e) => handleChange(index, 'name', e.target.value)}
                error={errors && errors[index]?.name}
                icon={<FaBuilding className="size-4" />}
              />
            </div>
            <div className="mb-4">
              <Input 
                label={"Número de Acionistas"}
                value={company.shareholderCount}
                onChange={(e) => handleChange(index, 'shareholderCount', e.target.value)}
                error={errors && errors[index]?.shareholderCount}
                icon={<FaFileCircleCheck className="size-4"/>}
                type='number'
                min='1'
                step='1'
              />
            </div>
          </div>
        );
      })}
      <div className="flex justify-between gap-2">
        <button
          onClick={prevStep}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 flex items-center gap-2 w-full justify-center"
          aria-label="Voltar para o passo anterior"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 9-3 3m0 0 3 3m-3-3h7.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          Voltar
        </button>
        <button
          onClick={handleNext}
          className="w-full flex justify-center items-center gap-x-2 rounded-md bg-[#0d6efd] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          aria-label="Ir para o próximo passo"
        >
          Seguinte
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m12.75 15 3-3m0 0-3-3m3 3h-7.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Step2;
